/**
 * @fileoverview Renders a multi-step progress bar.
 *
 * @author Benjamin Leitner (bleitner@yext.com)
 */

goog.declareModuleId('yext.ui.components.MultiStep');

import {MenuController} from '/ui/components/menu/menucontroller';
import {classNames} from '/ui/lib/classnames';
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/multistep/multistep.module.scss';
import ArrowDown from '/ui/icons/inline/arrow-down.svg';
import ArrowUp from '/ui/icons/inline/arrow-up.svg';
import * as a11yStyles from '/ui/utilities/a11y.module.scss';

/**
 * @typedef {{
 *   steps: !Array<string>,
 *   selectedStepIndex: !number,
 *   onChangeSelectedStepIndex?: (function(!number):void),
 *   compactLayout?: boolean,
 *   hasDropdown?: boolean,
 * }}
 */
export let MultiStepProps;

/**
 * @param {MultiStepProps} props
 * @return {React.ReactElement}
 */
export function MultiStep({
  steps,
  onChangeSelectedStepIndex,
  selectedStepIndex,
  compactLayout = false,
  hasDropdown = false,
}) {
  function translateStepName(index, value) {
    // TRANSLATORS: The arguments are the index of the step and the step name
    // EXAMPLE: 1. Name
    return yext.msg('{0}. {1}', index, value);
  }
  function translateStepIndicator(index, length) {
    // TRANSLATORS: The arguments are the index of the step and the number of steps
    // EXAMPLE: Step 1 of 2
    return yext.msg('(Step {0} of {1})', index, length);
  }
  const renderedSteps = steps.map((value, index) => {
    const isActive = index === selectedStepIndex;
    const stepClassName = isActive ? styles.stepSelected : styles.stepUnselected;
    return (
      <div
        key={index}
        className={styles.step}
        aria-current={isActive ? 'step' : undefined}
      >
        <div className={stepClassName}></div>
        {
            compactLayout
              ? <span className={a11yStyles.srOnly}>{translateStepName(index+1, value)}</span>
              : <span>{translateStepName(index+1, value)}</span>
        }
      </div>
    );
  });

  const stepActions = steps.map((value, index) => {
    return (
      {
        name: translateStepName(index+1, value),
        displayName: <span className={styles.actionName}>{translateStepName(index+1, value)}</span>,
        callback: () => onChangeSelectedStepIndex && onChangeSelectedStepIndex(index),
      }
    );
  });

  const trigger = function({dropdownExpanded, buttonRef, attributes}) {
    const triggerClass = classNames(
      attributes.className,
      styles.triggerContainer,
    );
    return (
      <button
        {...attributes}
        ref={buttonRef}
        type="button"
        className={triggerClass}
        id="multistep-mobile-dropdown-button"
        tid="multistep-mobile-dropdown-button"
      >
        <span className={styles.stepIndicatorWDropdownWrapper}>
          <span className={styles.stepIndicatorWDropdown}>
            {translateStepIndicator(selectedStepIndex + 1, steps.length)}
          </span>
          <span className={styles.trigger}>
            {dropdownExpanded
            ? <ArrowUp />
            : <ArrowDown />
            }
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {renderedSteps}
      </div>
      {compactLayout
          && (
            <div className={styles.stepNameContainer}>
              <span className={styles.stepName}>{translateStepName(selectedStepIndex + 1, steps[selectedStepIndex])}</span>
              <span className={styles.stepIndicatorContainer}>
                {hasDropdown
                  ? (
                    <MenuController
                      actions={stepActions}
                      label=""
                      trigger={trigger}
                    />
                  )
                  : (
                    <span className={styles.stepIndicatorWoDropdownWrapper}>
                      <span className={styles.stepIndicatorWoDropdown}>
                        {translateStepIndicator(selectedStepIndex + 1, steps.length)}
                      </span>
                    </span>
                  )
                }
              </span>
            </div>
          )
      }
    </div>
  );
}
